import { type Wrapper } from '@neo4j-labs/experimental-query-api-wrapper';
import type { ConnectionCredentials, DriverConnectInfo, TransactionMetadataConfig } from '@nx/constants';
import type { ExecuteQueryArgs } from '@nx/neo4j-sdk/src/types/sdk-types';
import { type TokenProvider } from '@nx/neo4j-token-provider';
import type { Driver } from 'neo4j-driver';
import type * as DriverCore from 'neo4j-driver-core';

import type { SessionConfig } from './neo4j-api';

export class TokenProviderError extends Error {
  static staticCode = 'Connection.TokenProviderError';

  code: string;

  constructor() {
    super('A token provider was not provided to the driver');
    this.name = 'DriverTokenProviderError';
    this.code = TokenProviderError.staticCode;
  }
}

export class DriverAuthenticationError extends Error {
  static code = 'Connection.AuthenticationError';

  code: string;

  constructor() {
    super('Driver is not authenticated');
    this.name = 'DriverAuthenticationError';
    this.code = DriverAuthenticationError.code;
  }
}

export class DriverConnectionError extends Error {
  static code = 'Connection.ConnectionError';

  code: string;

  constructor() {
    super('Driver is not connected');
    this.name = 'DriverConnectionError';
    this.code = DriverConnectionError.code;
  }
}

export class NoDatabaseSelectedError extends Error {
  static code = 'Connection.MissingDatabaseError';

  code: string;

  constructor() {
    super('No active database');
    this.name = 'ActiveDatabaseError';
    this.code = NoDatabaseSelectedError.code;
  }
}

export class OperationNotSupportedOnSystemDatabaseError extends Error {
  static code = 'Connection.OperationNotSupportedOnSystemDatabase';

  code: string;

  constructor(message = 'Operation is not supported on the system database') {
    super(message);
    this.name = 'OperationNotSupportedOnSystemDatabaseError';
    this.code = OperationNotSupportedOnSystemDatabaseError.code;
  }
}

export type RunQueryInput = {
  query: DriverCore.types.Query;
  parameters?: DriverCore.types.Parameters;
};

export type RunQueryConfig = {
  metadata: TransactionMetadataConfig;
  recordLimit?: ((newRecord: DriverCore.Record) => boolean) | number;
  sessionConfig: SessionConfig;
  signal?: AbortSignal;
  timeout?: DriverCore.TransactionConfig['timeout'];
  transactionCommitType?: 'implicit' | 'auto';
  transactionMode?: 'write' | 'read';
  useBookmarkManager?: boolean;
};

export type RunTypedQueryConfig = {
  metadata: TransactionMetadataConfig;
  database?: string;
  signal?: AbortSignal;
  timeout?: DriverCore.TransactionConfig['timeout'];
};

export type RunQueryResult<Entries extends DriverCore.RecordShape = DriverCore.RecordShape> = {
  records: DriverCore.Record<Entries>[];
  recordLimitHit: boolean;
  summary: DriverCore.ResultSummary;
};

export abstract class DriverAdapter {
  /**
   * Create driver instance
   */
  abstract connect(url: string, credentials: ConnectionCredentials, tokenProvider?: TokenProvider): Promise<void>;

  /**
   * Destroy driver instance
   */
  abstract disconnect(shouldClearSSOData: boolean): Promise<void>;

  /**
   * Verify if that the Neo4j system database is reachable
   */
  abstract healthCheck(): Promise<Error | null>;

  /**
   * Get auth token that were used to instantiate a driver instance.
   */
  abstract getAuthToken(): DriverCore.types.AuthToken | undefined;

  /**
   * Get URL and auth token that were used to instantiate a driver instance.
   */
  abstract getDriverConnectInfo(): DriverConnectInfo | null;

  /**
   * Get underlying Neo4j Driver instance
   *
   * This method is provided as an escape hatch, avoid using it if other means
   * to achieve desired result are available.
   */
  abstract getDriverInstance(): Driver | Wrapper | undefined;

  /**
   * Execute Cypher query
   *
   * @throws {DriverConnectionError} Throws if driver is not connected to database.
   */
  abstract runQuery<Entries extends DriverCore.RecordShape = DriverCore.RecordShape>(
    input: RunQueryInput,
    config: RunQueryConfig,
    enabledTools: string[],
  ): Promise<RunQueryResult<Entries>>;

  /**
   * Execute typed Cypher query, doesn't support auto commit or record limit
   *
   * @throws {DriverConnectionError} Throws if driver is not connected to database.
   */
  abstract runTypedQuery<T>(
    { query, queryConfig }: ExecuteQueryArgs<T>,
    { metadata, signal, database, timeout }: RunTypedQueryConfig,
    enabledTools: string[],
  ): Promise<T>;
}
